// features/admin/userMonitorApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Shared ───────────────────────────────────────────────────────────────────

export type UserType = 'registered' | 'guest';

// ─── App User Monitor ─────────────────────────────────────────────────────────

export interface AppUserProfile {
  user_id: number;
  full_name: string;
  email: string;
  contact: string;
  country_code: string | null;
  dob: string | null;
  anniversary: string | null;
  referral_code: string;
}

export interface AppUserDevice {
  device_id: string | null;
  device_name: string | null;
  os_version: string | null;
  model_number: string | null;
  push_enabled: boolean;
  app_installed: boolean;
}

export interface AppUserAccount {
  status: number;
  status_label: string;
  login_type: string;
  registered_at: string;
  last_active_at: string | null;
  days_since_active: number | null;
}

export interface AppUserMembership {
  plan_id: number | null;
  plan_name: string;
  member_label: string;
  subscription_date: string | null;
  expiry: string | null;
  recurring_label: string | null;
  is_expired: boolean | null;
}

export interface AppUserWallet {
  balance: number;
}

export interface AppUserReferrals {
  total_referrals: number;
  successful_referrals: number;
  total_reward_earned: number;
}

export interface AppUserTodayStats {
  orders: number;
  delivered: number;
  cancelled: number;
  spend: number;
  product_views: number;
  purchase_events: number;
}

export interface AppUserPeriodStats {
  from_date: string;
  to_date: string;
  orders: number;
  delivered: number;
  cancelled: number;
  spend: number;
  coupon_savings: number;
  cod_orders: number;
  pay_later_orders: number;
  wallet_orders: number;
  first_order_date: string | null;
  last_order_date: string | null;
  product_views: number;
  purchase_events: number;
  unique_products: number;
  categories_browsed: number;
}

export interface AppUserPerformance {
  total_orders: number;
  total_delivered: number;
  total_cancelled: number;
  total_failed_delivery: number;
  success_rate: number;
  total_spend: number;
  total_coupon_savings: number;
  total_wallet_used: number;
  total_reward_discount: number;
  cod_orders: number;
  pay_later_orders: number;
  first_order_date: string | null;
  last_order_date: string | null;
  total_views: number;
  total_purchase_events: number;
  unique_products: number;
  categories_browsed: number;
  first_activity: string | null;
  last_activity: string | null;
  rating: string | number | null;
}

export interface RecentOrder {
  order_id: number;
  invoice_no: string;
  status: string;
  status_code: number;
  amount: number;
  cod?: boolean;
  pay_later?: boolean;
  wallet_used?: boolean;
  order_date: string;
  delivered_at?: string | null;
}

export interface AppUserEntry {
  profile: AppUserProfile;
  device: AppUserDevice;
  account: AppUserAccount;
  membership: AppUserMembership;
  wallet: AppUserWallet;
  referrals: AppUserReferrals;
  today: AppUserTodayStats;
  period: AppUserPeriodStats;
  performance: AppUserPerformance;
  recent_orders: RecentOrder[];
}

export interface AppUserSummary {
  users: {
    total: number;
    active: number;
    inactive: number;
    app_installed: number;
    push_enabled: number;
    social_logins: number;
    new_today: number;
    new_this_week: number;
    new_this_month: number;
  };
  memberships: {
    subscribed: number;
    silver: number;
    gold: number;
    platinum: number;
  };
  today: {
    orders: number;
    delivered: number;
    cancelled: number;
    revenue: number;
    active_users: number;
    product_views: number;
    purchase_events: number;
    active_browsers: number;
  };
}

export interface AppUserMonitorRequest {
  user_id?: number;
  status?: 0 | 1;
  plan_id?: number;
  search?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
  download?: 0 | 1 | 2;
}

export interface AppUserMonitorResponse {
  error: boolean;
  message: string;
  summary: AppUserSummary;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  data: AppUserEntry[];
}

// ─── Web User Monitor ─────────────────────────────────────────────────────────

export interface WebUserProfile {
  user_type: UserType;
  user_id: number;
  full_name: string;
  email: string;
  contact: string;
  account_status: string;
  login_type: string;
  registered_at: string;
  last_seen_at: string | null;
  days_since_active: number | null;
}

export interface WebUserTodayStats {
  orders: number;
  delivered: number;
  spend: number;
  product_views: number;
  purchase_events: number;
}

export interface WebUserPeriodStats {
  from_date: string;
  to_date: string;
  orders: number;
  delivered: number;
  cancelled: number;
  spend: number;
  cod_orders: number;
  first_order_date: string | null;
  last_order_date: string | null;
  product_views: number;
  purchase_events: number;
  unique_products: number;
  categories_browsed: number;
}

export interface WebUserPerformance {
  total_orders: number;
  total_delivered: number;
  total_cancelled: number;
  success_rate: number;
  total_spend: number;
  coupon_savings: number;
  wallet_orders: number;
  pay_later_orders: number;
  first_order_date: string | null;
  last_order_date: string | null;
  total_views: number;
  total_purchases: number;
  unique_products: number;
  categories_browsed: number;
  first_view: string | null;
  last_view: string | null;
  rating: string | number | null;
}

export interface WebUserEntry {
  profile: WebUserProfile;
  today: WebUserTodayStats;
  period: WebUserPeriodStats;
  performance: WebUserPerformance;
  recent_orders: RecentOrder[];
}

export interface WebUserSummary {
  registered: {
    total: number;
    active: number;
    inactive: number;
    new_today: number;
    new_month: number;
  };
  guests: {
    total: number;
    new_today: number;
  };
  today: {
    reg_orders: number;
    reg_revenue: number;
    guest_orders: number;
    guest_revenue: number;
    total_orders: number;
    total_revenue: number;
    product_views: number;
    purchase_events: number;
    unique_visitors: number;
  };
}

export interface WebUserMonitorRequest {
  user_type?: 'registered' | 'guest';
  user_id?: number;
  guest_user_id?: number;
  status?: 0 | 1;
  search?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
  download?: 0 | 1 | 2;
}

export interface WebUserMonitorResponse {
  error: boolean;
  message: string;
  summary: WebUserSummary;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  data: WebUserEntry[];
}

// ─── Config ───────────────────────────────────────────────────────────────────

export const USER_MONITOR_ENDPOINTS = {
  APP_USER_MONITOR: '/admin/appuser-monitor-list',
  WEB_USER_MONITOR: '/admin/web-user-monitor-list',
} as const;

// ─── API Slice ────────────────────────────────────────────────────────────────

export const userMonitorApi = createApi({
  reducerPath: 'userMonitorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['AppUsers', 'WebUsers'],
  endpoints: (build) => ({

    // ── App User Monitor ──
    appUserMonitorList: build.query<AppUserMonitorResponse, AppUserMonitorRequest | void>({
      query: (params) => ({
        url:    USER_MONITOR_ENDPOINTS.APP_USER_MONITOR,
        method: 'POST',
        body:   params || {},
      }),
      providesTags: ['AppUsers'],
      transformResponse: (response: any): AppUserMonitorResponse => ({
        error:      response?.error      ?? false,
        message:    response?.message    || '',
        summary:    response?.summary    || { users: {}, memberships: {}, today: {} },
        pagination: response?.pagination || { total: 0, page: 1, limit: 20, pages: 0 },
        data:       Array.isArray(response?.data) ? response.data : [],
      }),
      transformErrorResponse: (response: any) => ({
        error:      true,
        message:    response.data?.message || 'Failed to load app user monitor data',
        summary:    { users: {}, memberships: {}, today: {} },
        pagination: { total: 0, page: 1, limit: 20, pages: 0 },
        data:       [],
      }),
    }),

    // ── Web User Monitor ──
    webUserMonitorList: build.query<WebUserMonitorResponse, WebUserMonitorRequest | void>({
      query: (params) => ({
        url:    USER_MONITOR_ENDPOINTS.WEB_USER_MONITOR,
        method: 'POST',
        body:   params || {},
      }),
      providesTags: ['WebUsers'],
      transformResponse: (response: any): WebUserMonitorResponse => ({
        error:      response?.error      ?? false,
        message:    response?.message    || '',
        summary:    response?.summary    || { registered: {}, guests: {}, today: {} },
        pagination: response?.pagination || { total: 0, page: 1, limit: 20, pages: 0 },
        data:       Array.isArray(response?.data) ? response.data : [],
      }),
      transformErrorResponse: (response: any) => ({
        error:      true,
        message:    response.data?.message || 'Failed to load web user monitor data',
        summary:    { registered: {}, guests: {}, today: {} },
        pagination: { total: 0, page: 1, limit: 20, pages: 0 },
        data:       [],
      }),
    }),

  }),
});

export const {
  useAppUserMonitorListQuery,
  useWebUserMonitorListQuery,
} = userMonitorApi;